/**
 * SEO Utilities for handling hreflang tags and canonical URLs
 */

// Base domain for the site
const BASE_URL = 'https://tideasolis.com';

// Supported languages
const SUPPORTED_LANGUAGES = ['en', 'sk', 'hu'];

/**
 * Generate the canonical URL for the current page
 * @param {string} path - Current path without language prefix
 * @param {string} currentLanguage - Current language code
 * @returns {string} Canonical URL
 */
export const getCanonicalUrl = (path, currentLanguage = 'en') => {
  // Remove any query parameters
  const cleanPath = path.split('?')[0];
  
  // Remove language prefix if present
  let pathWithoutLang = cleanPath;
  SUPPORTED_LANGUAGES.forEach(lang => {
    if (pathWithoutLang.startsWith(`/${lang}/`)) {
      pathWithoutLang = pathWithoutLang.substring(3); // Remove /{lang}/
    }
  });
  
  // For non-English languages, add the language prefix
  const langPrefix = currentLanguage === 'en' ? '' : `/${currentLanguage}`;
  
  // Handle the root path
  if (pathWithoutLang === '/' || pathWithoutLang === '') {
    return `${BASE_URL}${langPrefix}`;
  }
  
  return `${BASE_URL}${langPrefix}${pathWithoutLang}`;
};

/**
 * Generate hreflang URLs for all supported languages
 * @param {string} path - Current path without language prefix
 * @returns {Array} Array of objects with language code and URL
 */
export const getHreflangUrls = (path) => {
  // Remove any query parameters
  const cleanPath = path.split('?')[0];
  
  // Remove language prefix if present
  let pathWithoutLang = cleanPath;
  SUPPORTED_LANGUAGES.forEach(lang => {
    if (pathWithoutLang.startsWith(`/${lang}/`)) {
      pathWithoutLang = pathWithoutLang.substring(3); // Remove /{lang}/
    }
  });
  
  // Handle the root path
  if (pathWithoutLang === '/' || pathWithoutLang === '') {
    return SUPPORTED_LANGUAGES.map(lang => ({
      lang,
      url: lang === 'en' ? BASE_URL : `${BASE_URL}/${lang}`
    }));
  }
  
  // Generate URLs for all languages
  return SUPPORTED_LANGUAGES.map(lang => ({
    lang,
    url: lang === 'en' 
      ? `${BASE_URL}${pathWithoutLang}` 
      : `${BASE_URL}/${lang}${pathWithoutLang}`
  }));
};

/**
 * Get language-specific meta tags for SEO
 * @param {string} path - Current path
 * @param {string} currentLanguage - Current language code
 * @returns {Object} Object containing canonical URL and hreflang URLs
 */
export const getSeoTags = (path, currentLanguage = 'en') => {
  return {
    canonicalUrl: getCanonicalUrl(path, currentLanguage),
    hreflangUrls: getHreflangUrls(path)
  };
}; 